import {FC, useMemo, useState, useCallback, useEffect} from 'react';
import styled from 'styled-components';
import {useParams} from 'wouter';

import {groupBy} from '@shared/lib/utils/array_utils';
import {DoorMessageCategory, FrontendDoorMessage} from '@shared/models/api_models';

import {notifyError} from '@shared_web/lib/notification';

import {AlertSymbol} from '@mavismart_frontend/components/alert_symbol';
import {TableWrapper} from '@mavismart_frontend/components/common_styles';
import {DateText} from '@mavismart_frontend/components/date_text';
import {InputSearch} from '@mavismart_frontend/components/input_search';
import {Loader} from '@mavismart_frontend/components/loader';
import {ToggleSwitch} from '@mavismart_frontend/components/toggle_switch';
import {
  findTranslationKey,
  translate,
  Translate,
  TranslateKey,
} from '@mavismart_frontend/components/translate';
import {apiCall} from '@mavismart_frontend/lib/api';
import {GenericCell} from '@mavismart_frontend/pages/cells/generic_cell';
import {DoorDetailMessage} from '@mavismart_frontend/pages/tabs/door_detail_message';
import {getUserLanguage, useAccount} from '@mavismart_frontend/stores/account_store';

interface DoorMessagesProps {
  baseUrl: string;
  doorId: string;
}

interface GroupedMessage {
  category: DoorMessageCategory;
  translatedCategory: string;
  translateKey: TranslateKey | undefined;
  mostRecentMessage: FrontendDoorMessage;
  messages: FrontendDoorMessage[];
  to: string;
}

export const DoorMessages: FC<DoorMessagesProps> = ({baseUrl, doorId}) => {
  const {category} = useParams<{category?: DoorMessageCategory}>();
  const [toggleState, setToggleState] = useState(false);
  const [messages, setMessages] = useState<FrontendDoorMessage[] | undefined>(undefined);

  const account = useAccount();

  const handleToggleChange = useCallback((checked: boolean) => {
    setToggleState(checked);
  }, []);

  useEffect(() => {
    if (category === undefined) {
      apiCall('POST /door-messages-summary', {id: doorId})
        .then(responses => {
          const allMessages = Object.values(responses.messages).flat();
          setMessages(allMessages);
        })
        .catch(notifyError);
    }
  }, [doorId, category]);

  const [filteredMessages, setFilteredMessages] = useState<GroupedMessage[]>([]);
  const hasAtLeastOneAlert = useMemo(() => {
    return filteredMessages.some(group => group.mostRecentMessage.level !== 'green');
  }, [filteredMessages]);

  // Group messages by category and sort by most recent message
  const groupedMessages: GroupedMessage[] | undefined = useMemo(() => {
    if (!messages) {
      return undefined;
    }

    const grouped = groupBy(messages, 'category');
    const res = [...grouped.entries()]
      .map(([category, messages]) => {
        messages.sort((a, b) => b.ts - a.ts);
        const translateKey = findTranslationKey(category);
        return {
          category,
          mostRecentMessage: messages[0],
          to: `${baseUrl}/${category}`,
          messages,
          translateKey,
          translatedCategory: translateKey
            ? translate(translateKey, getUserLanguage(account))
            : category,
        };
      })
      .filter(group => toggleState === false || group.mostRecentMessage.level !== 'green')
      .sort((g1, g2) => g2.mostRecentMessage.ts - g1.mostRecentMessage.ts);
    setFilteredMessages(res);
    return res;
  }, [messages, account, baseUrl, toggleState]);

  const getLabel = useCallback((item: GroupedMessage) => item.translatedCategory, []);

  return (
    <>
      {category ? (
        <DoorDetailMessage category={category} doorId={doorId} />
      ) : (
        <>
          {groupedMessages ? (
            <>
              {filteredMessages.length === 0 ? (
                <Translate value="no_message_found" />
              ) : (
                <>
                  <InputSearch
                    items={groupedMessages}
                    setFilteredItems={setFilteredMessages}
                    getLabel={getLabel}
                  />
                  {hasAtLeastOneAlert && (
                    <WrapperToggle>
                      <Translate value="only_show_alerts_anomalies" />
                      <ToggleSwitch checked={toggleState} onChange={handleToggleChange} />
                    </WrapperToggle>
                  )}
                  <TableWrapper>
                    {filteredMessages.map(group => {
                      const {category, mostRecentMessage, translateKey, to} = group;
                      const {ts, level} = mostRecentMessage;
                      return (
                        <GenericCell
                          key={`${ts}-${category}`}
                          id={`${ts}-${category}`}
                          title={
                            <>
                              {translateKey ? <Translate value={translateKey} /> : <>{category}</>}
                            </>
                          }
                          icon={<AlertSymbol level={level} />}
                          subtitle={<DateText date={new Date(ts)} />}
                          to={to}
                        />
                      );
                    })}
                  </TableWrapper>
                </>
              )}
            </>
          ) : (
            <Loader />
          )}
        </>
      )}
    </>
  );
};

DoorMessages.displayName = 'DoorMessages';

const WrapperToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 10px ;
`;
